import { Suspense, use, useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { useI18n } from "../i18n/index.js";
import { captureError } from "../lib/analytics.js";
import { isApiError } from "../lib/api.js";
import { AlertIcon, DecisionIcon, LinkButton, SpinnerIcon } from "../ui/index.js";

export function LoadingState({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="dt-state dt-state--loading" role="status" aria-live="polite">
      <span className="dt-state__icon">
        <SpinnerIcon />
      </span>
      <span className="dt-state__hint">{label ?? t("common.loading")}</span>
    </div>
  );
}

/**
 * The part of a page that waits for its loader's data (see `deferred`): the page itself
 * shows as soon as the link is clicked, and `fallback` stands in here until the data lands.
 * The fallback is that part's skeleton (`components/skeletons.tsx`), never a spinner.
 * A rejection reaches the route's ErrorBoundary, as a loader that threw would.
 */
export function Deferred<T>({ data, fallback, children }: { data: Promise<T>; fallback: React.ReactNode; children: (value: T) => React.ReactNode }) {
  return (
    <Suspense fallback={fallback}>
      <Resolved data={data} render={children} />
    </Suspense>
  );
}

function Resolved<T>({ data, render }: { data: Promise<T>; render: (value: T) => React.ReactNode }) {
  return render(use(data));
}

export function EmptyState({ title, hint, action, icon }: { title: string; hint?: string; action?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="dt-state" data-testid="empty-state">
      <span className="dt-state__icon">{icon ?? <DecisionIcon size={18} />}</span>
      <p className="dt-state__title">{title}</p>
      {hint && <p className="dt-state__hint">{hint}</p>}
      {action && <div className="dt-state__action">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title,
  code,
  message,
  requestId,
  action,
}: {
  title?: string | undefined;
  code?: string | undefined;
  message?: string | undefined;
  requestId?: string | undefined;
  action?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="dt-state dt-state--error" role="alert" data-testid="error-state">
      <span className="dt-state__icon">
        <AlertIcon size={18} />
      </span>
      <p className="dt-state__title">{title ?? t("error.title")}</p>
      {message && <p className="dt-state__hint">{message}</p>}
      {code && (
        <p className="dt-subtle" style={{ fontSize: "var(--dt-text-xs)" }}>
          <code data-testid="error-code">{code}</code>
        </p>
      )}
      {requestId && (
        <p className="dt-subtle" style={{ fontSize: "var(--dt-text-xs)" }}>
          {t("error.requestId", { id: requestId })}
        </p>
      )}
      {action && <div className="dt-state__action">{action}</div>}
    </div>
  );
}

/** Consistent rendering for thrown responses and typed API errors on any route. */
export function RouteErrorBoundary() {
  const { t } = useI18n();
  const err = useRouteError();
  // A failed request is control-api's to report; anything else broke here.
  const unexpected = !isRouteErrorResponse(err) && !isApiError(err);
  useEffect(() => {
    if (unexpected) captureError(err);
  }, [err, unexpected]);
  const home = (
    <LinkButton to="/" variant="secondary" size="sm">
      {t("error.backToWorkspaces")}
    </LinkButton>
  );
  let content: React.ReactNode;
  if (isRouteErrorResponse(err)) {
    const body = (err.data ?? {}) as { code?: string; message?: string };
    content = (
      <ErrorState
        title={err.status === 404 ? t("error.notFound") : t("error.status", { status: err.status })}
        code={body.code}
        message={body.message ?? err.statusText}
        action={home}
      />
    );
  } else if (isApiError(err)) {
    const title =
      err.code === "workspace_forbidden" || err.code === "forbidden"
        ? t("error.forbidden")
        : err.code === "not_found"
          ? t("error.notFound")
          : err.code === "network_error"
            ? t("error.network")
            : t("error.requestFailed");
    content = <ErrorState title={title} code={err.code} message={err.message} requestId={err.requestId} action={home} />;
  } else {
    content = <ErrorState message={err instanceof Error ? err.message : String(err)} action={home} />;
  }
  return <div className="dt-boundary">{content}</div>;
}
